import { useEffect, useMemo, useState } from "react";

import { getTrainersTeam } from "@fitness-app/app-store/src/store/reducers/team/selectors";
import { userSelectors } from "@fitness-app/app-store";
import { TeamMemberStatus } from "@fitness-app/data-models/entities/TeamMember";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { createClientsAccess } from "~/modules/Trainee/clientsAccess";
import { useAppSelector } from "~/store/initializeStore";

export const useTrainersFilters = (
  selectedTab: string,
  showOnlyActive = false,
  { showAllTrainers = false }: { showAllTrainers?: boolean } = {},
) => {
  const { memberId } = useUserClaims();
  const permissions = useAppSelector(userSelectors.getUserPermissions);
  const access = createClientsAccess(permissions);
  const assignedTrainers = useAppSelector((store) => store.team.assignedTrainers);

  const [showOnlyMyTrainers, toggleMyTrainerFilters] = useState(true);
  const trainers = useAppSelector(getTrainersTeam);

  useEffect(() => {
    if (selectedTab !== "active") {
      toggleMyTrainerFilters(true);
    }
  }, [selectedTab]);

  const filteredTrainers = useMemo(() => {
    // Bez prawa filtrowania po trenerze lista nie ma odbiorcy — własne
    // podopieczne i tak dopina zapytanie.
    if (!access.canFilterByTrainer) {
      return [];
    }

    const activeOnly = (list: typeof trainers) =>
      showOnlyActive ? list.filter((trainer) => trainer.status === TeamMemberStatus.ACTIVE) : list;

    // Zakres tenantowy (ACCOUNT_OWNER, administracja, sprzedaż, support) filtruje
    // po dowolnej trenerce; przecięcie z „moimi podopiecznymi" dawało im pustą
    // albo jednoosobową listę i kolumna traciła filtr.
    if (access.readsWholeTenant || !showOnlyMyTrainers) {
      return activeOnly(trainers);
    }

    const assignedTrainersIds = assignedTrainers.map((trainer) => trainer.assignedTrainerId);

    let filtered = trainers.filter((trainer) => assignedTrainersIds.includes(trainer.id) || trainer.id === memberId);
    if (showAllTrainers) {
      filtered = [
        ...filtered,
        ...trainers.filter((trainer) => !assignedTrainersIds.includes(trainer.id) && trainer.id !== memberId),
      ];
    }
    return activeOnly(filtered);
  }, [
    access.canFilterByTrainer,
    access.readsWholeTenant,
    showAllTrainers,
    showOnlyMyTrainers,
    trainers,
    showOnlyActive,
    assignedTrainers,
    memberId,
  ]);

  return {
    toggleMyTrainerFilters,
    showOnlyMyTrainers,
    trainers: filteredTrainers,
    /** Przełącznik zakresu pokazuje tylko ten, kto ma co przełączać (TEAM). */
    canToggleAssignedTrainers: access.canToggleAssignedTrainers,
  };
};
